import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Tag } from "@models/common/tag";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterParams } from "@models/grid/grid-filter";
import { Trust } from "@models/trusts/trust";
import { TrustStatus } from "@models/trusts/trusts-enums";
import {
  ExportEntityListModalComponent
} from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { ABNPipe } from "@shared/pipes/abnPipe";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import { deepClone } from "fast-json-patch/commonjs/core";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class TrustsExportService {
  modalFormsService = inject(ModalFormsService);

  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  getExcelTitleRows: (numberColumnForExport: number) => ExcelRow[] = (numberColumnForExport) =>
    excelTitleForReport(numberColumnForExport, 'Trusts Report');

  exportParamsXls(numberColumnForExport: number = 0): ExcelExportParams {
    return {
      ...this.exportParams(),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }

  exportParamsCsv() {
    return this.exportParams();
  }

  exportParams(): CsvExportParams {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const companyData = params.node?.data as Trust;
        if (params.column.getColId() === 'name') {
          return companyData.name;
        } else if (params.column.getColId() === 'abn') {
          return ABNPipe.formatValue(companyData.abn);
        } else if (params.column.getColId() === 'status') {
          return TrustStatus[value as TrustStatus];
        } else if (params.column.getColId() === 'tags') {
          return (value as Tag[] ?? []).map((tag: Tag) => tag.name).join(', ');
        } else if (params.column.getColId() === 'systemTags') {
          return (value as Tag[] ?? []).map((tag: Tag) => tag.name).join(', ');
        }
        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      sheetName: 'Trusts',
    } as CsvExportParams;
  }

  exportSelectedTrustsToXls(
    gridApi: GridApi,
    colDefs: ColDef[],
    isBulkExport: boolean,
    checkedTrust: Trust[],
    trustList: Trust[],
    exportType: ExportTypeEnum = ExportTypeEnum.EXCEL,
  ): void {
    this.exportTrustList(
      gridApi,
      colDefs,
      isBulkExport,
      exportType,
      checkedTrust,
      trustList
    );
  }

  exportSelectedTrustsToCsv(
    gridApi: GridApi,
    colDefs: ColDef[],
    isBulkExport: boolean,
    checkedTrust: Trust[],
    trustList: Trust[],
    exportType: ExportTypeEnum = ExportTypeEnum.CSV,
  ): void {
    this.exportTrustList(
      gridApi,
      colDefs,
      isBulkExport,
      exportType,
      checkedTrust,
      trustList
    );
  }

  exportTrustProfile(gridApi: GridApi, trust: Trust, colDefs: ColDef[]): void {
    const columnForExport = colDefs
      ?.filter(c => !c.hide && c.headerName)
      .map(c => c.field) as string[];

    const nameIndex = columnForExport.findIndex(field => field === 'name');
    if (nameIndex >= 0) {
      columnForExport.splice(nameIndex + 1, 0, 'abn');
    }
    const tagIndex = columnForExport.findIndex(field => field === 'tags');
    if (tagIndex >= 0) {
      columnForExport.splice(tagIndex + 1, 0, 'systemTags');
    }

    const exportParamsXls = this.exportParamsXls(columnForExport.length - 1) as ExcelExportParams;
    gridApi.exportDataAsExcel({ ...exportParamsXls, columnKeys: columnForExport, onlySelected: true });
  }

  getSelectedFilterOptions(gridApi: GridApi, columnId: string): string {
    const filterParams = gridApi.getColumn(columnId)?.getColDef().filterParams as FilterParams;
    const filterOptions = filterParams?.filterOptions ?? [];
    const selectedFilterOptions = filterOptions.filter(o => o.active).map(o => o.label).join(', ');
    return selectedFilterOptions;
  }

  exportTrustList(
    gridApi: GridApi,
    colDefs: ColDef[],
    isBulkExport: boolean,
    exportType: ExportTypeEnum,
    checkedTrust: Trust[],
    trustList: Trust[],
  ): void {
    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.colDefs = deepClone(colDefs) as ColumnWithExportName[];
    componentInstance.title = 'Export Trust List';
    componentInstance.subTitle = 'trust selected';
    componentInstance.numberOfCompanies = isBulkExport ? checkedTrust.length : trustList.length;
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const nameIndex = columnForExport.findIndex(field => field === 'name');
        if (nameIndex >= 0) {
          columnForExport.splice(nameIndex + 1, 0, 'abn');
        }

        const tagIndex = columnForExport.findIndex(field => field === 'tags');
        if (tagIndex >= 0) {
          columnForExport.splice(tagIndex + 1, 0, 'systemTags');
        }

        const numberColumnForExport = columnForExport.length - 1;

        const fileName = this.getSelectedFilterOptions(gridApi, 'status')
          ? this.getSelectedFilterOptions(gridApi, 'status') + ' Trusts ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US')
          : 'Trusts ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US');

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          const exportParamsXls = this.exportParamsXls(numberColumnForExport) as ExcelExportParams;
          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv({ ...params, fileName });
        }
      });
  }
}
